import { Link as RouterLink } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';

import { usersApi } from '../api/users';
import { useAuthStore } from '../store/authStore';
import { Leaguemate } from '../types';

/**
 * "Rate Your Leaguemates" — a focused worklist of every distinct
 * Sleeper account the user has shared a league with, sorted by
 * total shared leagues (most shared at the top). Each row links
 * straight to that user's public profile with `?rate=1` so the
 * rating dialog auto-opens; rows the user has already rated swap
 * the Rate button for a green "Rated" chip but stay clickable so
 * you can revisit your review.
 *
 * Surfaced from the homepage hero CTA when the viewer is verified
 * — turns the "what should I do next?" answer into a single link.
 */
export default function LeaguematesPage() {
  const { user } = useAuthStore();

  const { data, isLoading, error } = useQuery({
    queryKey: ['leaguemates'],
    queryFn: usersApi.getLeaguemates,
    enabled: !!user?.isVerified,
  });

  // Unverified users can navigate here directly via the URL — make
  // sure they understand they need to verify first.
  if (user && !user.isVerified) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', textAlign: 'center', py: 6 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5 }}>
          Verify your Sleeper account first
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          We need to know which leagues are yours before we can list your
          leaguemates.
        </Typography>
        <Button
          variant="contained"
          color="warning"
          component={RouterLink}
          to="/dashboard?verify=1"
        >
          Verify Now
        </Button>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography color="error">
          Couldn't load your leaguemates. Try refreshing.
        </Typography>
      </Box>
    );
  }

  const leaguemates = data ?? [];

  // Empty state for verified users with no leaguemates yet — usually
  // means their leagues haven't been synced or they're new to the app.
  if (leaguemates.length === 0) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', textAlign: 'center', py: 6 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5 }}>
          No leaguemates yet
        </Typography>
        <Typography color="text.secondary">
          We haven't found any leaguemates for your linked Sleeper account
          yet. If you just verified, your leagues are still syncing in the
          background — check back in a minute.
        </Typography>
      </Box>
    );
  }

  const rated = leaguemates.filter((l) => l.hasRated).length;
  const remaining = leaguemates.length - rated;

  return (
    <Box sx={{ maxWidth: 880, mx: 'auto' }}>
      {/* Page header — frame the work clearly so the user knows what
          this page is for and how much of it they've finished. */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Rate Your Leaguemates
        </Typography>
        <Typography color="text.secondary">
          Every Sleeper user you've shared a league with, sorted by how
          many leagues you've played together. Tap{' '}
          <strong>Rate</strong> to drop a review.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Chip
            label={`${leaguemates.length} leaguemate${leaguemates.length === 1 ? '' : 's'}`}
            size="small"
            variant="outlined"
          />
          {rated > 0 && (
            <Chip
              icon={<CheckCircleIcon />}
              label={`${rated} rated`}
              size="small"
              color="success"
              variant="outlined"
            />
          )}
          {remaining > 0 && (
            <Chip
              label={`${remaining} to go`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Stack>
      </Box>

      <Stack spacing={1.5}>
        {leaguemates.map((lm) => (
          <LeaguemateRow key={lm.sleeperAccount.id} leaguemate={lm} />
        ))}
      </Stack>
    </Box>
  );
}

function LeaguemateRow({ leaguemate }: { leaguemate: Leaguemate }) {
  const { sleeperAccount, sharedLeagueCount, sharedLeagues, hasRated } =
    leaguemate;
  // Build a compact "2026 · 2025 · 2024" season summary for context
  // without listing every league name in the row. Distinct seasons,
  // newest first.
  const seasons = Array.from(
    new Set(sharedLeagues.map((l) => l.season))
  ).sort((a, b) => parseInt(b, 10) - parseInt(a, 10));

  const profileHref = `/sleeper/${sleeperAccount.sleeper_user_id}${
    hasRated ? '' : '?rate=1'
  }`;

  return (
    <Card
      variant="outlined"
      sx={{
        transition: 'border-color 0.15s, transform 0.15s',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 1.5,
          '&:last-child': { pb: 1.5 },
        }}
      >
        <Avatar
          src={
            sleeperAccount.current_avatar_id
              ? `https://sleepercdn.com/avatars/thumbs/${sleeperAccount.current_avatar_id}`
              : undefined
          }
          sx={{ width: 48, height: 48 }}
        >
          {sleeperAccount.current_username?.[0]?.toUpperCase() ?? '?'}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            component={RouterLink}
            to={`/sleeper/${sleeperAccount.sleeper_user_id}`}
            sx={{
              textDecoration: 'none',
              color: 'text.primary',
              fontWeight: 600,
              fontSize: 16,
              '&:hover': { color: 'primary.main' },
            }}
          >
            {sleeperAccount.current_username}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.25 }}
          >
            {sharedLeagueCount} shared league{sharedLeagueCount === 1 ? '' : 's'}
            {seasons.length > 0 && (
              <>
                {' · '}
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {seasons.slice(0, 4).join(' · ')}
                  {seasons.length > 4 && ` +${seasons.length - 4}`}
                </Box>
              </>
            )}
          </Typography>
        </Box>

        {hasRated ? (
          <Button
            component={RouterLink}
            to={profileHref}
            variant="outlined"
            color="success"
            size="small"
            startIcon={<CheckCircleIcon />}
            endIcon={<ArrowForwardIcon />}
            sx={{ flexShrink: 0 }}
          >
            Rated
          </Button>
        ) : (
          <Button
            component={RouterLink}
            to={profileHref}
            variant="contained"
            color="secondary"
            size="small"
            startIcon={<StarIcon />}
            sx={{ flexShrink: 0, minWidth: 96 }}
          >
            Rate
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
